import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LevelAnimalIcon from '../../studentDashboard/components/LevelAnimalIcon';
import { getLevelForPoints } from '../../../config/levelsConfig';
import { useDailyTest } from '../hooks/useDailyTest';
import DragonFlyby from '../../aiTutor/DragonFlyby';
// Deep, not the barrel: the barrel carries the speaking PAGE, which the
// dashboard must not pull into its chunk.
import { DailySpeakingCard } from '../../dailySpeaking/components/DailySpeakingCard';
import { QuizSkillPeek } from './QuizSkillPeek';
import './DailyTestCard.css';

// What the dragon says on the dashboard. The name goes in front of it.
const DRAGON_MESSAGE = 'try the quiz today.';

const SEEN_KEY = 'dailyTest:seen:';

function readSeen(studentId) {
  try {
    return localStorage.getItem(SEEN_KEY + studentId) === '1';
  } catch {
    return false;
  }
}

function writeSeen(studentId, seen) {
  try {
    if (seen) localStorage.setItem(SEEN_KEY + studentId, '1');
    else localStorage.removeItem(SEEN_KEY + studentId);
  } catch {
    // Private mode / quota — the hint is an optimisation, never load-bearing.
  }
}

/** Holds the fetch back until the browser is idle so it never races the dashboard's own requests. */
function useIdle() {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (typeof requestIdleCallback !== 'function') {
      const timer = setTimeout(() => setIdle(true), 300);
      return () => clearTimeout(timer);
    }
    const handle = requestIdleCallback(() => setIdle(true), { timeout: 2000 });
    return () => cancelIdleCallback(handle);
  }, []);
  return idle;
}

/**
 * Dashboard entry point for the daily test.
 *
 * Renders nothing at all unless the server says this student has a test today,
 * so students outside the rollout see no change.
 *
 * @param {{ studentId: string, isTeacherView?: boolean }} props
 */
/**
 * @param {{ studentId: string, studentName?: string, isTeacherView?: boolean, dragon?: boolean }} props -
 *   `dragon` flies the Ender Dragon in over the dashboard with a nudge to do the
 *   quiz. Rendered from here rather than the dashboard because only this card
 *   knows whether there IS a quiz today — a dragon asking for one that does not
 *   exist would be the first thing that taught a child to ignore him.
 *   `studentName` is what he calls her. On a child's own phone the admin-only
 *   name cache is empty, so without this he has nothing but an id — and he
 *   drops the name rather than read one out.
 */
/** The day's two quizzes, as the child sees them. 每日口语 is DailySpeakingCard. */
const SECTION_COPY = {
  listening: { title: '每日听力', hint: '听一听，选一选、写一写' },
  readwrite: { title: '每日读写', hint: '排句子、填单词' },
};

export function DailyTestCard({ studentId, studentName, isTeacherView = false, dragon = false, points = 0 }) {
  const idle = useIdle();
  const { data } = useDailyTest(idle ? studentId : null);

  // Students who had a card last time get its space reserved up front, so the
  // assignment list below does not jump when the deferred fetch lands.
  const [expected] = useState(() => readSeen(studentId));

  // One card per section still to do today (每日听力, 每日读写), each showing how
  // many questions it holds. A section with nothing to serve is never owed.
  const served = data?.served || {};
  const sections = data?.enabled === true
    ? (data.owed || []).filter((s) => SECTION_COPY[s] && served[s] > 0)
    : [];

  useEffect(() => {
    if (data) writeSeen(studentId, sections.length > 0);
  }, [data, sections.length, studentId]);

  // The third card, 每日口语. Its own feature and its own fetch, deferred on the
  // same idle signal; it renders nothing for a child off its rollout.
  const speaking = (
    <DailySpeakingCard studentId={studentId} ready={idle} isTeacherView={isTeacherView} points={points} />
  );

  if (!sections.length) {
    return (
      <>
        {expected && !data ? <div className="dt-card-placeholder" aria-hidden="true" /> : null}
        {speaking}
      </>
    );
  }

  // The child's own level animal: cuter and more encouraging than a 🧠.
  const level = getLevelForPoints(points);
  const body = (section) => (
    <>
      <span className="dt-card-icon" aria-hidden="true">
        <LevelAnimalIcon level={level.level} icon={level.icon} />
      </span>
      <span className="dt-card-info">
        <span className="dt-card-title">{SECTION_COPY[section].title}</span>
        <span className="dt-card-secondary">{served[section]} 题 · {SECTION_COPY[section].hint}</span>
      </span>
      <QuizSkillPeek skill={section} />
    </>
  );

  // A teacher previewing a student sees the same card but it does not navigate —
  // following the link would open the test AS that student, and submitting would
  // record answers in their name.
  const cards = sections.map((section) => (isTeacherView ? (
    <div key={section} className={`dt-card dt-card--${section} dt-card-readonly`}>{body(section)}</div>
  ) : (
    <Link
      key={section}
      to={`/daily-test/${studentId}?date=${data.date}&section=${section}`}
      // The finish screen shows the child's own level animal from these.
      state={{ points }}
      className={`dt-card dt-card--${section}`}
    >
      {body(section)}
    </Link>
  )));

  return (
    <>
      {dragon && !isTeacherView && (
        <DragonFlyby enabled studentId={studentId} studentName={studentName} message={DRAGON_MESSAGE} />
      )}
      {cards}
      {speaking}
    </>
  );
}

export default DailyTestCard;
